// Apple Custom Product Pages (created 2026-10-13, App Store Connect), one per surface group. OneLink
// forwards `af_ios_store_cpp=<id>` to the App Store as `ppid=<id>` (verified 2026-10-13 with an iPhone
// user agent), and App Store Connect reports downloads per page. That is how an iOS install is tied to a
// surface now that OneLink no longer passes campaign tokens to the store (see useAppStoreLink.ts).
//
// Pure on purpose: the id derives from the OneLink's `c`, which derives from the path, so
// staticOnelinkUrl() stays byte-identical on the server and the client. No device detection, no window.
// Imported by bioLinks.ts as well, which is why this file imports nothing.
const CPP_INVITE = "89be0859-06b7-4c08-a619-4a48f3519e7c";
const CPP_TRIP_SHARE = "6cba271e-29c4-436e-b603-98aef0e8012e";
const CPP_WEBSITE = "672c3236-f446-4ce5-a655-01547ab3f79c";
const CPP_LINK_IN_BIO = "dab81b28-f7f6-455b-a8be-842b4590d275";
const CPP_OTHER = "956383e9-c6d2-4f8f-9ab4-b68a0e41cb78";
// Paid ChatGPT Ads traffic (the ad link carries utm_campaign=chatgpt_ads), created 2026-10-14.
const CPP_CHATGPT_ADS = "fcb2c719-04cf-4924-8b89-2e45b42d14e7";
// "Web - Blog", created 2026-10-23 so blog installs are counted apart from the rest of the website (the install-card test).
const CPP_BLOG = "44a4ff11-98b4-4452-8914-ead1d0778b89";

// Path-based `c` values plus the `utm_campaign` values the web and app share builders actually put on
// links (a landing utm_campaign becomes `c`), so a real invite or share lands on its own page, not "Other".
const CPP_BY_CAMPAIGN = new Map<string, string>([
	["join_link", CPP_INVITE],
	["trip_invite", CPP_INVITE],
	["general_invite", CPP_INVITE],
	["referral", CPP_INVITE],
	["trip_share_page", CPP_TRIP_SHARE],
	["web_route_preview", CPP_TRIP_SHARE],
	["web_list_preview", CPP_TRIP_SHARE],
	["profile_page", CPP_TRIP_SHARE],
	["trip_share_link", CPP_TRIP_SHARE],
	["profile_share_link", CPP_TRIP_SHARE],
	["template_share_link", CPP_TRIP_SHARE],
	["discover_share_link", CPP_TRIP_SHARE],
	["creator_trips", CPP_TRIP_SHARE],
	["creator_profile", CPP_TRIP_SHARE],
	["web_home", CPP_WEBSITE],
	["web_feature_lp", CPP_WEBSITE],
	["web_blog", CPP_BLOG],
	["web_discover", CPP_WEBSITE],
	["web_auth", CPP_WEBSITE],
	["blogtrips", CPP_WEBSITE],
	["stippl_website", CPP_WEBSITE],
	["website", CPP_WEBSITE],
	["stippl_articles", CPP_WEBSITE],
	["articles", CPP_WEBSITE],
	["bio_links", CPP_LINK_IN_BIO],
	["chatgpt_ads", CPP_CHATGPT_ADS],
]);

/** The Custom Product Page id for a OneLink `c` value. Anything unknown (web_other, a utm_campaign override, no `c`) is "Other". */
export function cppIdForCampaign(c: string | null): string {
	if (c === null) return CPP_OTHER;
	if (c.startsWith("links_")) return CPP_LINK_IN_BIO;
	return CPP_BY_CAMPAIGN.get(c) ?? CPP_OTHER;
}

const PARAM = "af_ios_store_cpp";

/**
 * Returns `url` with `af_ios_store_cpp` set from its own `c` (added if absent, replaced if present,
 * never duplicated). The query is edited as text rather than through `searchParams.set`, which would
 * re-serialise every other param (a space becomes "+", not "%20") and change the href of every CTA.
 */
export function withCustomProductPage(url: string): string {
	try {
		const parsed = new URL(url);
		const id = cppIdForCampaign(parsed.searchParams.get("c"));
		const others = parsed.search.slice(1).split("&").filter((pair) => pair !== "" && pair.split("=")[0] !== PARAM);
		parsed.search = [...others, `${PARAM}=${id}`].join("&");
		return parsed.toString();
	} catch (e: any) {
		console.error("[AppsFlyer] withCustomProductPage failed to parse URL:", { url, error: e?.message || e });
		return url;
	}
}
